import type { UserGroup } from '@gmn-portal/backend';
import { Users } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserGroups } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import {
  GROUP_SERVICE_ORDER,
  HELI_PROVIDER_GROUP_KEY,
  SCOPE_OPTIONS,
  serviceRegistry,
  sortUserGroupsByKey,
} from '@/shared/utils/permissions';

type GroupRow = {
  id: string;
  key: string;
  domain: 'gmn' | 'customer';
  group: UserGroup;
};

const getGroupLabel = (t: ReturnType<typeof useIntl>['t'], key: string) => {
  const labelKey = `portalRoles.${key}`;
  const label = t(labelKey as unknown as never);
  return label === labelKey ? key : label;
};

export function UserGroupsOverview() {
  const { t } = useIntl();
  const { data, isLoading } = useUserGroups({ limit: 0, sort: 'key' });
  const groups = (data?.docs || []) as UserGroup[];

  const groupRows = useMemo<GroupRow[]>(
    () =>
      groups.map((group) => ({
        id: group.id,
        key: group.key,
        domain: group.domain as GroupRow['domain'],
        group,
      })),
    [groups]
  );

  const orderedRows = useMemo(() => sortUserGroupsByKey(groupRows), [groupRows]);
  const gmnRows = orderedRows.filter((row) => row.domain === 'gmn');
  const customerRows = orderedRows.filter(
    (row) => row.domain === 'customer' && row.key !== HELI_PROVIDER_GROUP_KEY
  );
  const heliProviderRows = orderedRows.filter((row) => row.key === HELI_PROVIDER_GROUP_KEY);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupKey = searchParams.get('group');

  const selectedRow = useMemo(() => {
    if (!orderedRows.length) return null;
    if (!selectedGroupKey) return orderedRows[0];
    return orderedRows.find((row) => row.key === selectedGroupKey) ?? orderedRows[0];
  }, [orderedRows, selectedGroupKey]);

  useEffect(() => {
    if (orderedRows.length === 0) return;
    if (selectedGroupKey) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('group', orderedRows[0].key);
    setSearchParams(nextParams, { replace: true });
  }, [orderedRows, searchParams, selectedGroupKey, setSearchParams]);

  const selectedGroup = selectedRow?.group;

  const services = GROUP_SERVICE_ORDER.filter(
    (service) => serviceRegistry[service].includeInGroupsUi
  );

  return (
    <PageContainer>
      <PageTitle
        icon={Users}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('navigation.items.gmnSettings')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{t('userGroups.title')}</SimpleBreadcrumbItem>
          </>
        }
        title={t('userGroups.title')}
      />

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t('userGroups.listTitle')}</CardTitle>
            <CardDescription>{t('userGroups.listDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHead>{t('userGroups.group')}</TableHead>
                  <TableHead>{t('userGroups.domain')}</TableHead>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {orderedRows.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      className="text-muted-foreground text-sm py-6 text-center"
                    >
                      {t('userGroups.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {gmnRows.length > 0 && (
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell
                      colSpan={2}
                      className="py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {t('userGroups.domainGmn')}
                    </TableCell>
                  </TableRow>
                )}
                {gmnRows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => {
                      const nextParams = new URLSearchParams(searchParams);
                      nextParams.set('group', row.key);
                      setSearchParams(nextParams, { replace: true });
                    }}
                    className={cn('cursor-pointer', selectedRow?.id === row.id && 'bg-muted/40')}
                  >
                    <TableCell className="font-medium">{getGroupLabel(t, row.key)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{t('userGroups.domainGmn')}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {customerRows.length > 0 && (
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell
                      colSpan={2}
                      className="py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {t('userGroups.domainCustomer')}
                    </TableCell>
                  </TableRow>
                )}
                {customerRows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => {
                      const nextParams = new URLSearchParams(searchParams);
                      nextParams.set('group', row.key);
                      setSearchParams(nextParams, { replace: true });
                    }}
                    className={cn('cursor-pointer', selectedRow?.id === row.id && 'bg-muted/40')}
                  >
                    <TableCell className="font-medium">{getGroupLabel(t, row.key)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{t('userGroups.domainCustomer')}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {heliProviderRows.length > 0 && (
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell
                      colSpan={2}
                      className="py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {t('userGroups.domainHeliProvider')}
                    </TableCell>
                  </TableRow>
                )}
                {heliProviderRows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => {
                      const nextParams = new URLSearchParams(searchParams);
                      nextParams.set('group', row.key);
                      setSearchParams(nextParams, { replace: true });
                    }}
                    className={cn('cursor-pointer', selectedRow?.id === row.id && 'bg-muted/40')}
                  >
                    <TableCell className="font-medium">{getGroupLabel(t, row.key)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{t('userGroups.domainCustomer')}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('userGroups.detailsTitle')}</CardTitle>
            <CardDescription>{t('userGroups.detailsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!selectedGroup ? (
              <div className="text-sm text-muted-foreground">{t('userGroups.selectGroup')}</div>
            ) : (
              <>
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t('permissions.matrixTitle')}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t('permissions.matrixDescription')}
                  </p>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableHeaderRow>
                          <TableHead className="w-[180px]">{t('permissions.service')}</TableHead>
                          <TableHead className="text-center px-2">
                            {t('permissions.create')}
                          </TableHead>
                          <TableHead className="text-center px-2">
                            {t('permissions.read')}
                          </TableHead>
                          <TableHead className="text-center px-2">
                            {t('permissions.update')}
                          </TableHead>
                          <TableHead className="text-center px-2">
                            {t('permissions.delete')}
                          </TableHead>
                        </TableHeaderRow>
                      </TableHeader>
                      <TableBody>
                        {services.map((service) => {
                          const entry = selectedGroup.permissions?.find(
                            (perm) => perm.service === service
                          );
                          return (
                            <TableRow key={service}>
                              <TableCell className="font-medium">
                                {t(serviceRegistry[service].labelKey as unknown as never)}
                              </TableCell>
                              {(['create', 'read', 'update', 'delete'] as const).map((action) => (
                                <TableCell key={action} className="text-center px-2">
                                  <div className="inline-flex w-full justify-center">
                                    <Checkbox checked={Boolean(entry?.[action])} disabled />
                                  </div>
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t('permissions.scopesTitle')}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t('permissions.scopesDescription')}
                  </p>
                  <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                    <li>{t('permissions.scopesHelp.all')}</li>
                    <li>{t('permissions.scopesHelp.customerAssignment')}</li>
                    <li>{t('permissions.scopesHelp.userAssignment')}</li>
                    <li>{t('permissions.scopesHelp.explicitApproval')}</li>
                  </ul>
                  <div className="space-y-3">
                    {services.map((service) => {
                      const scopes = (selectedGroup.scopes || [])
                        .filter((scope) => scope.service === service)
                        .map((scope) => scope.scope);
                      const allowedScopes = serviceRegistry[service].allowedScopes ?? SCOPE_OPTIONS;
                      const visibleScopes =
                        selectedGroup.domain === 'gmn'
                          ? allowedScopes.filter((scope) => scope !== 'customer-assignment')
                          : allowedScopes;
                      return (
                        <div key={service} className="rounded-lg border border-border p-3">
                          <div className="text-sm font-medium mb-2">
                            {t(serviceRegistry[service].labelKey as unknown as never)}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {visibleScopes.map((scope) => (
                              <Badge
                                key={scope}
                                variant={scopes.includes(scope) ? 'secondary' : 'outline'}
                              >
                                {t(`permissions.scopes.${scope}` as unknown as never)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
